/**
 * 初始化 product-card 轮播
 * @param {string|HTMLElement} trackSelector - track 元素的选择器或元素本身
 */
export function initProductCarousel(trackSelector) {
  const track = typeof trackSelector === 'string' 
    ? document.querySelector(trackSelector) 
    : trackSelector;
  
  if (!track) return;

  const isCountertop = track.classList.contains('product-card__track--countertop') || track.id === 'countertopTrack';
  const slideSelector = isCountertop ? '.product-card__slide--countertop' : '.product-card__slide';
  const slides = Array.from(track.querySelectorAll(slideSelector));
  const carousel = track.closest('.product-carousel') || track.parentElement;
  const prevBtn = carousel?.querySelector('.product-card__nav--prev');
  const nextBtn = carousel?.querySelector('.product-card__nav--next');
  const viewport = carousel?.querySelector('.product-card__viewport');
  const dots = Array.from(carousel?.querySelectorAll('.product-dots .dot') || []);

  if (slides.length === 0) return;

  let currentIndex = 0;

  // 切换到指定幻灯片
  function goToSlide(index) {
    if (isCountertop) {
      if (index < 0) index = 0;
      if (index >= slides.length) index = slides.length - 1;
    } else {
      if (index < 0) index = slides.length - 1;
      if (index >= slides.length) index = 0;
    }

    currentIndex = index;
    if (isCountertop && viewport) {
      const target = slides[index];
      const left = target.offsetLeft;
      viewport.scrollTo({ left, behavior: 'smooth' });
    } else {
      const offset = -index * 100;
      track.style.transform = `translateX(${offset}%)`;
    }

    // 更新 slides 的 is-active 状态
    slides.forEach((slide, i) => {
      slide.classList.toggle('is-active', i === currentIndex);
    });

    // 更新 dots 的 is-active 状态
    dots.forEach((dot, i) => {
      dot.classList.toggle('is-active', i === currentIndex);
    });

    if (isCountertop) {
      if (prevBtn) prevBtn.disabled = currentIndex === 0;
      if (nextBtn) nextBtn.disabled = currentIndex === slides.length - 1;
    }
  }

  function syncIndexWithScroll() {
    if (!isCountertop || !viewport) return;
    let nearestIndex = 0;
    let minDist = Infinity;
    slides.forEach((s, i) => {
      const dist = Math.abs(viewport.scrollLeft - s.offsetLeft);
      if (dist < minDist) {
        minDist = dist;
        nearestIndex = i;
      }
    });
    currentIndex = nearestIndex;
    updateCountertopDisabledState();
  }

  function updateCountertopDisabledState() {
    if (!isCountertop || !viewport) return;
    const tolerance = 4;
    const atStart = viewport.scrollLeft <= tolerance;
    const atEnd = viewport.scrollLeft + viewport.clientWidth >= track.scrollWidth - tolerance;
    if (prevBtn) prevBtn.disabled = atStart;
    if (nextBtn) nextBtn.disabled = atEnd;
  }

  // 绑定箭头按钮事件
  if (prevBtn) {
    prevBtn.addEventListener('click', () => {
      if (prevBtn.disabled) return;
      goToSlide(currentIndex - 1);
      updateCountertopDisabledState();
    });
  }

  if (nextBtn) {
    nextBtn.addEventListener('click', () => {
      if (nextBtn.disabled) return;
      goToSlide(currentIndex + 1);
      updateCountertopDisabledState();
    });
  }

  // 绑定 dots 点击事件
  dots.forEach((dot, i) => {
    dot.addEventListener('click', () => {
      goToSlide(i);
    });
  });

  // 添加触摸滑动功能（避免与台面款的 scroll-snap 冲突）
  if (!isCountertop) {
    setupTouchSwipe(track, goToSlide, () => currentIndex);
  }

  if (isCountertop) {
    updateCountertopDisabledState();
    let scrollTimer = null;
    viewport.addEventListener('scroll', () => {
      if (scrollTimer) window.clearTimeout(scrollTimer);
      scrollTimer = window.setTimeout(syncIndexWithScroll, 60);
      updateCountertopDisabledState();
    }, { passive: true });

    const resolveContainerMaxWidth = () => {
      const probe = document.createElement('div');
      probe.style.cssText = 'position:absolute;visibility:hidden;height:0;width:var(--container-max-width);';
      document.body.appendChild(probe);
      const w = probe.offsetWidth || 0;
      document.body.removeChild(probe);
      return w;
    };
    const styleId = 'countertop-nav-hide-style';
    const ensureStyle = (on) => {
      let styleEl = document.getElementById(styleId);
      if (on) {
        if (!styleEl) {
          styleEl = document.createElement('style');
          styleEl.id = styleId;
          styleEl.textContent = `.product-card__track--countertop::after{content:none !important;} .product-card__nav--countertop{display:none !important;} .product-card__slide--countertop{flex:0 0 calc((100% - 4rem * 3)/4) !important;width:calc((100% - 4rem * 3)/4) !important;} .product-card__track--countertop {gap:4rem !important;}`;
          document.head.appendChild(styleEl);
        }
      } else if (styleEl) {
        styleEl.remove();
      }
    };
    const apply = () => {
      const maxW = resolveContainerMaxWidth();
      ensureStyle(window.innerWidth >= maxW);
    };
    apply();
    window.addEventListener('resize', apply);
  }
}

/**
 * 设置触摸滑动功能
 * @param {HTMLElement} track - track 元素
 * @param {Function} goToSlide - 切换幻灯片的函数
 * @param {Function} getCurrentIndex - 获取当前索引的函数
 */
function setupTouchSwipe(track, goToSlide, getCurrentIndex) {
  let touchStartX = 0;
  let touchStartY = 0;
  let touchEndX = 0;
  let touchEndY = 0;
  let isDragging = false;
  const swipeThreshold = 50; // 最小滑动距离（像素）
  const maxVerticalSwipe = 30; // 最大垂直滑动距离，超过此值则不触发水平滑动

  track.addEventListener('touchstart', (e) => {
    touchStartX = e.touches[0].clientX;
    touchStartY = e.touches[0].clientY;
    isDragging = true;
  }, { passive: true });

  track.addEventListener('touchmove', (e) => {
    if (!isDragging) return;
    touchEndX = e.touches[0].clientX;
    touchEndY = e.touches[0].clientY;
  }, { passive: true });

  track.addEventListener('touchend', () => {
    if (!isDragging) return;
    isDragging = false;

    const deltaX = touchEndX - touchStartX;
    const deltaY = Math.abs(touchEndY - touchStartY);

    // 如果垂直滑动距离太大，不触发水平滑动
    if (deltaY > maxVerticalSwipe) return;

    const currentIndex = getCurrentIndex();
    // 向右滑动（显示上一张）
    if (deltaX > swipeThreshold) {
      goToSlide(currentIndex - 1);
    }
    // 向左滑动（显示下一张）
    else if (deltaX < -swipeThreshold) {
      goToSlide(currentIndex + 1);
    }
  }, { passive: true });
}
